import React, { useState, useEffect, useContext } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import QuickStartGuide from '../components/QuickStartGuide';
import { AuthContext } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { getCustomer, submitSupportRequest } from '../utils/api';

const SupportPage = () => {
        const { user } = useContext(AuthContext);
        const { showToast } = useToast();
        const [customer, setCustomer] = useState(null);
        const [subject, setSubject] = useState('');
        const [message, setMessage] = useState('');
        const [submitting, setSubmitting] = useState(false);
        const [submitted, setSubmitted] = useState(false);
        const [loading, setLoading] = useState(true);

        useEffect(() => {
                const fetchData = async () => {
                        if (user && user.customerId) {
                                try {
                                        const customerRes = await getCustomer(user.customerId);
                                        if (!customerRes.ok)
                                                throw new Error("Customer not found");
                                        const customerData = await customerRes.json();
                                        setCustomer(customerData.data);
                                } catch (err) {
                                        console.error(err);
                                } finally {
                                        setLoading(false);
                                }
                        } else {
                                setLoading(false);
                        }
                };
                fetchData();
        }, [user]);

        const handleSubmit = async (e) => {
                e.preventDefault();
                setSubmitting(true);
                try {
                        const res = await submitSupportRequest({ subject, message });
                        if (!res.ok) {
                                throw new Error('Failed to submit');
                        }
                        setSubject('');
                        setMessage('');
                        setSubmitted(true);
                        showToast('success', 'Support request submitted successfully');
                } catch (err) {
                        console.error('Failed to submit support request:', err);
                        showToast('error', 'Failed to submit support request');
                } finally {
                        setSubmitting(false);
                }
        };

        if (loading) {
                return <div className="page-gradient min-h-screen flex items-center justify-center">Loading...</div>;
        }

        return (
                <div className="page-gradient min-h-screen">
                        <Header customer={customer} />
                        <main className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
                                <div className="bg-white/80 backdrop-blur-sm p-6 sm:p-8 card-shadow rounded-2xl mb-8">
                                        <h1 className="text-2xl font-bold mb-4">Welcome Aboard</h1>
                                        <p className="mb-4 font-bold">Congratulations on your new Cleverplant!</p>
                                        <p className="mb-4">As the name suggests, it helps you connect with your plants and keep them thriving.</p>

                                        <h2 className="text-xl font-bold mb-2">What's included</h2>
                                        <ul className="list-disc list-inside mb-4">
                                                <li>Your Cleverplant device</li>
                                                <li>Access to the Cleverplant Portal</li>
                                                <li>A Quick Guide for easy setup</li>
                                        </ul>
                                        <h2 className="text-xl font-bold mb-2">Setup</h2>
                                        <p className="mb-4">Follow the steps of your Quick Guide. Once Cleverplant is connected to Wi-Fi and placed in a plant, it gets to work by giving your plant a voice.</p>

                                        <h2 className="text-xl font-bold mb-2">Power & charging</h2>
                                        <p className="mb-4">Cleverplant is built for the long run. It has a long-life battery and recharges via its integrated solar panel.
                                                <br />No battery changes needed. We’ve got you covered.</p>

                                        <h2 className="text-xl font-bold mb-2">Notifications</h2>
                                        <p className="mb-4">Your selected email address will subscribe to alerts from your Cleverplant(s). You’ll be notified if a plant needs attention.</p>

                                        <h2 className="text-xl font-bold mb-2">We’re here for you</h2>
                                        <p className="mb-4">If you have questions or if something feels unusual, don’t hesitate to contact us.
                                                <br /><br /><b>Let’s keep in touch!</b></p>
                                        <img src="images/logo_text_color.png" width="300px;" />
                                </div>

                                <div className="bg-white/80 backdrop-blur-sm p-6 sm:p-8 card-shadow rounded-2xl">
                                        <h1 className="text-2xl font-bold mb-6">Contact Support</h1>
                                        {submitted ? (
                                                <div className="text-center py-6">
                                                        <p className="text-lg font-semibold mb-2">We have received your support request!</p>
                                                        <p className="text-gray-600">Our team will get back to you as soon as possible. Thank you for reaching out.</p>
                                                </div>
                                        ) : (
                                                <form onSubmit={handleSubmit} className="space-y-4">
                                                        <div>
                                                                <label htmlFor="subject" className="block text-sm font-medium text-text-strong mb-1">Subject</label>
                                                                <input
                                                                        id="subject"
                                                                        data-testid="support-subject"
                                                                        type="text"
                                                                        value={subject}
                                                                        onChange={(e) => setSubject(e.target.value)}
                                                                        required
                                                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent outline-none"
                                                                        placeholder="Brief description of your issue"
                                                                />
                                                        </div>
                                                        <div>
                                                                <label htmlFor="message" className="block text-sm font-medium text-text-strong mb-1">Message</label>
                                                                <textarea
                                                                        id="message"
                                                                        data-testid="support-message"
                                                                        value={message}
                                                                        onChange={(e) => setMessage(e.target.value)}
                                                                        required
                                                                        rows={6}
                                                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent outline-none resize-y"
                                                                        placeholder="Please describe your issue or question in detail"
                                                                />
                                                        </div>
                                                        <button
                                                                type="submit"
                                                                data-testid="support-submit"
                                                                disabled={submitting}
                                                                className="bg-brand text-white px-6 py-2 rounded-lg hover:bg-brand/90 transition-colors disabled:opacity-50"
                                                        >
                                                                {submitting ? 'Submitting...' : 'Submit'}
                                                        </button>
                                                </form>
                                        )}
                                </div>
                                {/*< QuickStartGuide /> */}
                        </main>
                        <Footer />
                </div>
        );
};

export default SupportPage;
